import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Modal from '../../components/Modal';
import PDFViewerModal from '../../components/PDFViewerModal';
import RichTextEditor from '../../components/RichTextEditor';
import Tooltip from '../../components/Tooltip';
import { useData } from '../../context/DataContext';
import { moduleMaterialsAPI } from '../../services/api';
import './ModuleMaterials.css';

const emptyForm = {
  studyId: '',
  courseId: '',
  title: '',
  description: '',
  type: 'document',
  articleContent: '',
  externalUrl: '',
  sortOrder: 0,
  isActive: true,
  tags: []
};

const parseBool = (value) => String(value) === 'true';

const TYPE_META = {
  document: { icon: '📄', label: 'Document',  color: '#1e40af', bg: '#dbeafe' },
  media:    { icon: '🎬', label: 'Media',      color: '#166534', bg: '#dcfce7' },
  article:  { icon: '📝', label: 'Artikel',    color: '#92400e', bg: '#fef3c7' },
  link:     { icon: '🔗', label: 'Link',       color: '#4c1d95', bg: '#ede9fe' },
};

const TAG_PALETTE = [
  { bg: '#e0f2fe', color: '#0369a1' },
  { bg: '#dcfce7', color: '#166534' },
  { bg: '#fef9c3', color: '#854d0e' },
  { bg: '#fce7f3', color: '#9d174d' },
  { bg: '#ede9fe', color: '#5b21b6' },
  { bg: '#ffedd5', color: '#9a3412' },
];

const tagColor = (tag) => {
  const idx = [...tag].reduce((a, c) => a + c.charCodeAt(0), 0) % TAG_PALETTE.length;
  return TAG_PALETTE[idx];
};

const ModuleMaterials = () => {
  const { studies, courses } = useData();

  const [materials, setMaterials] = useState([]);
  const [filterStudy, setFilterStudy] = useState('');
  const [filterCourse, setFilterCourse] = useState('');
  const [filterInstructor, setFilterInstructor] = useState('');
  const [filterTag, setFilterTag] = useState('');
  const [filterActive, setFilterActive] = useState('active');
  const [filterType, setFilterType] = useState('');
  const [filterFileStatus, setFilterFileStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ok, setOk] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [previewItem, setPreviewItem] = useState(null);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [pdfViewerUrl, setPdfViewerUrl] = useState('');
  const [pdfViewerTitle, setPdfViewerTitle] = useState('PDF bekijken');
  const [pdfViewerItemId, setPdfViewerItemId] = useState(null);
  const [integritySummary, setIntegritySummary] = useState({ totalFiles: 0, missingFiles: 0, recoveredPaths: 0 });
  const [form, setForm] = useState(emptyForm);
  const [uploadFile, setUploadFile] = useState(null);
  const [tagInput, setTagInput] = useState('');

  const getCohortDisplayName = (cohort) => {
    const name = String(cohort?.name || '').trim();
    const code = String(cohort?.code || '').trim();
    if (!name) return code;
    if (!code) return name;
    if (name.toLowerCase().includes(code.toLowerCase())) return name;
    return `${name} (${code})`;
  };

  const getStudyOptionLabel = (study) => {
    const cohortNames = (study.cohorts || []).map(getCohortDisplayName).filter(Boolean);
    return cohortNames.length > 0 ? `${study.name} – ${cohortNames.join(', ')}` : study.name;
  };

  const studiesWithCourses = useMemo(() =>
    (studies || []).filter((study) =>
      (study.studyYears || []).some((year) =>
        (year.modules || []).some((mod) => (mod.courses || []).length > 0)
      )
    ), [studies]);

  const filterCourseOptions = useMemo(() => {
    const sourceStudies = filterStudy
      ? studiesWithCourses.filter((s) => s.id === filterStudy)
      : studiesWithCourses;
    const seen = new Set();
    const list = [];
    sourceStudies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((mod) => {
          (mod.courses || []).forEach((course) => {
            const cid = course.id || course._id;
            if (!seen.has(cid)) {
              seen.add(cid);
              list.push({ id: cid, name: course.name, code: course.code });
            }
          });
        });
      });
    });
    return list;
  }, [filterStudy, studiesWithCourses]);

  const formCourseOptions = useMemo(() => {
    const sourceStudies = form.studyId
      ? studiesWithCourses.filter((s) => s.id === form.studyId)
      : studiesWithCourses;
    const seen = new Set();
    const list = [];
    sourceStudies.forEach((study) => {
      (study.studyYears || []).forEach((year) => {
        (year.modules || []).forEach((mod) => {
          (mod.courses || []).forEach((course) => {
            const cid = course.id || course._id;
            if (!seen.has(cid)) {
              seen.add(cid);
              list.push({ id: cid, name: course.name, code: course.code });
            }
          });
        });
      });
    });
    return list;
  }, [form.studyId, studiesWithCourses]);

  // All unique tags across loaded materials (for the filter dropdown)
  const allTags = useMemo(() => {
    const set = new Set();
    materials.forEach((m) => (m.tags || []).forEach((t) => set.add(t)));
    return Array.from(set).sort();
  }, [materials]);

  const instructorOptions = useMemo(() => {
    const map = new Map();
    (courses || []).forEach((course) => {
      const instructor = course?.instructor;
      const instructorId = String(instructor?.id || instructor?._id || '');
      if (!instructorId) return;
      if (!map.has(instructorId)) {
        map.set(instructorId, {
          id: instructorId,
          name: instructor?.name || [instructor?.firstName, instructor?.lastName].filter(Boolean).join(' ') || 'Onbekende docent'
        });
      }
    });
    return Array.from(map.values()).sort((left, right) => left.name.localeCompare(right.name, 'nl'));
  }, [courses]);

  const courseInstructorMap = useMemo(() => {
    const map = new Map();
    (courses || []).forEach((course) => {
      const courseId = String(course.id || course._id || '');
      if (!courseId) return;
      const instructorId = String(course?.instructor?.id || course?.instructor?._id || '');
      map.set(courseId, instructorId);
    });
    return map;
  }, [courses]);

  // Client-side tag + active filter
  const visibleMaterials = useMemo(() => {
    let list = materials;
    if (searchTerm.trim()) {
      const q = searchTerm.trim().toLowerCase();
      list = list.filter((m) => {
        const haystack = [m.title, m.description, m.course?.name, m.course?.code, ...(m.tags || [])]
          .filter(Boolean)
          .join(' ')
          .toLowerCase();
        return haystack.includes(q);
      });
    }
    if (filterFileStatus === 'missing') {
      list = list.filter((m) => m.file?.filename && m.fileIntegrity?.exists === false);
    }
    if (filterFileStatus === 'healthy') {
      list = list.filter((m) => !m.file?.filename || m.fileIntegrity?.exists !== false);
    }
    if (filterType) list = list.filter((m) => m.type === filterType);
    if (filterInstructor) {
      list = list.filter((m) => {
        const materialCourseId = String(m.course?.id || m.course?._id || m.course || '');
        if (!materialCourseId) return false;
        return String(courseInstructorMap.get(materialCourseId) || '') === String(filterInstructor);
      });
    }
    if (filterTag) list = list.filter((m) => (m.tags || []).includes(filterTag));
    if (filterActive === 'active') list = list.filter((m) => m.isActive !== false);
    else if (filterActive === 'inactive') list = list.filter((m) => m.isActive === false);
    return list;
  }, [materials, searchTerm, filterFileStatus, filterType, filterInstructor, filterTag, filterActive, courseInstructorMap]);

  const loadMaterials = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = { includeInactive: 'true' };
      if (filterCourse) params.courseId = filterCourse;
      if (filterInstructor) params.instructorId = filterInstructor;
      params.integrity = 'true';
      const response = await moduleMaterialsAPI.getAll(params);
      setMaterials(response.materials || []);
      setIntegritySummary(response.integritySummary || { totalFiles: 0, missingFiles: 0, recoveredPaths: 0 });
    } catch (loadError) {
      setError(loadError.message || 'Leermateriaal laden mislukt');
    } finally {
      setLoading(false);
    }
  }, [filterCourse, filterInstructor]);

  useEffect(() => {
    loadMaterials();
  }, [loadMaterials]);

  useEffect(() => {
    return () => {
      if (pdfViewerUrl) {
        URL.revokeObjectURL(pdfViewerUrl);
      }
    };
  }, [pdfViewerUrl]);

  const handleFilterStudyChange = (value) => {
    setFilterStudy(value);
    setFilterCourse('');
    setFilterTag('');
  };

  // --- Tag input ---
  const commitTag = useCallback(() => {
    const tag = tagInput.trim().toLowerCase().replace(/\s+/g, '-');
    if (tag && !form.tags.includes(tag)) {
      setForm((prev) => ({ ...prev, tags: [...prev.tags, tag] }));
    }
    setTagInput('');
  }, [tagInput, form.tags]);

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitTag();
    } else if (e.key === 'Backspace' && !tagInput && form.tags.length) {
      setForm((prev) => ({ ...prev, tags: prev.tags.slice(0, -1) }));
    }
  };

  const removeTag = useCallback((tag) => {
    setForm((prev) => ({ ...prev, tags: prev.tags.filter((t) => t !== tag) }));
  }, []);

  // --- Modal ---
  const openCreate = () => {
    setError('');
    setOk('');
    setEditingId(null);
    setForm({ ...emptyForm, studyId: filterStudy, courseId: filterCourse });
    setUploadFile(null);
    setTagInput('');
    setShowModal(true);
  };

  const openEdit = (item) => {
    setError('');
    setOk('');
    setEditingId(item.id);
    const courseId = item.course?.id || item.course?._id || String(item.course || '');
    let studyId = '';
    outer: for (const study of studiesWithCourses) {
      for (const year of study.studyYears || []) {
        for (const mod of year.modules || []) {
          if ((mod.courses || []).some((c) => String(c.id || c._id) === courseId)) {
            studyId = study.id;
            break outer;
          }
        }
      }
    }
    setForm({
      studyId,
      courseId,
      title: item.title || '',
      description: item.description || '',
      type: item.type || 'document',
      articleContent: item.articleContent || '',
      externalUrl: item.externalUrl || '',
      sortOrder: item.sortOrder || 0,
      isActive: item.isActive !== false,
      tags: item.tags || [],
    });
    setUploadFile(null);
    setTagInput('');
    setShowModal(true);
  };

  const saveMaterial = async (event) => {
    event.preventDefault();
    setError('');
    setOk('');
    try {
      const { studyId: _s, tags, ...rest } = form;
      if (!rest.courseId) { setError('Kies een vak'); return; }
      const payload = { ...rest, tags: JSON.stringify(tags) };
      if (editingId) {
        await moduleMaterialsAPI.update(editingId, payload, uploadFile);
        setOk('Leermateriaal bijgewerkt');
      } else {
        await moduleMaterialsAPI.create(payload, uploadFile);
        setOk('Leermateriaal toegevoegd');
      }
      setShowModal(false);
      await loadMaterials();
    } catch (saveError) {
      setError(saveError.message || 'Opslaan mislukt');
    }
  };

  const removeMaterial = async (id) => {
    if (!window.confirm('Weet je zeker dat je dit leermateriaal wilt verwijderen?')) return;
    setError('');
    setOk('');
    try {
      await moduleMaterialsAPI.delete(id);
      setOk('Leermateriaal verwijderd');
      await loadMaterials();
    } catch (deleteError) {
      setError(deleteError.message || 'Verwijderen mislukt');
    }
  };

  const closePdfViewer = () => {
    setIsPdfViewerOpen(false);
    setPdfViewerItemId(null);
    if (pdfViewerUrl) {
      URL.revokeObjectURL(pdfViewerUrl);
      setPdfViewerUrl('');
    }
  };

  const downloadBlob = (blob, fileName) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName || 'download';
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handleFilePreview = async (item) => {
    if (!item?.id) return;
    if (item.fileIntegrity?.exists === false) {
      setError('Bestand ontbreekt op server. Upload het materiaal opnieuw via Bewerken.');
      return;
    }

    try {
      setError('');
      const { blob, contentType, filename } = await moduleMaterialsAPI.fetchFile(item.id, 'view');
      const isPdf = /pdf/i.test(contentType || '') || /\.pdf$/i.test(filename || item.file?.originalName || item.file?.filename || '');

      if (!isPdf) {
        // Non-PDF files are downloaded directly to keep preview UX reliable.
        downloadBlob(blob, filename || item.file?.originalName || item.file?.filename || item.title || 'bestand');
        return;
      }

      if (pdfViewerUrl) {
        URL.revokeObjectURL(pdfViewerUrl);
      }

      const objectUrl = URL.createObjectURL(blob);
      setPdfViewerUrl(objectUrl);
      setPdfViewerTitle(item.title || 'PDF bekijken');
      setPdfViewerItemId(item.id);
      setIsPdfViewerOpen(true);
    } catch (fileError) {
      setError(fileError.message || 'Bestand bekijken mislukt');
    }
  };

  const handleFileDownload = async (itemOrId) => {
    const itemId = typeof itemOrId === 'string' ? itemOrId : itemOrId?.id;
    if (!itemId) return;

    const item = typeof itemOrId === 'string'
      ? (materials.find((m) => m.id === itemOrId) || previewItem)
      : itemOrId;

    if (item?.fileIntegrity?.exists === false) {
      setError('Bestand ontbreekt op server. Upload het materiaal opnieuw via Bewerken.');
      return;
    }

    try {
      setError('');
      const { blob, filename } = await moduleMaterialsAPI.fetchFile(itemId, 'download');
      downloadBlob(blob, filename || item?.file?.originalName || item?.file?.filename || item?.title || 'bestand');
    } catch (fileError) {
      setError(fileError.message || 'Bestand downloaden mislukt');
    }
  };

  return (
    <div className="module-materials-page">
      {/* Page header */}
      <div className="mm-page-header">
        <div>
          <h1 className="mm-page-title">Studiemateriaal</h1>
          <p className="mm-page-subtitle">
            Beheer leermateriaal per vak. Filter op studie, vak of tag om snel het juiste materiaal te vinden.
          </p>
        </div>
        <button className="btn-primary mm-add-btn" onClick={openCreate}>
          <span>＋</span> Materiaal toevoegen
        </button>
      </div>

      {error && <p className="mm-message mm-error">{error}</p>}
      {ok && <p className="mm-message mm-ok">{ok}</p>}

      {integritySummary.missingFiles > 0 && (
        <p className="mm-message mm-warn">
          Let op: {integritySummary.missingFiles} bestand(en) ontbreken op de server. Filter op "Bestandsstatus: Ontbreekt" om ze direct te zien.
        </p>
      )}

      {/* Filter bar */}
      <div className="mm-filter-bar">
        <div className="mm-filter-group">
          <label className="mm-filter-label">
            Studie
            <Tooltip text="Filter het overzicht op een specifieke studie. Kies eerst een studie om de vakkenlijst te verkleinen." />
          </label>
          <select className="form-input" value={filterStudy} onChange={(e) => handleFilterStudyChange(e.target.value)}>
            <option value="">Alle studies</option>
            {[...studiesWithCourses]
              .sort((a, b) => getStudyOptionLabel(a).localeCompare(getStudyOptionLabel(b), 'nl'))
              .map((s) => (
                <option key={s.id} value={s.id}>{getStudyOptionLabel(s)}</option>
              ))}
          </select>
        </div>

        <div className="mm-filter-group">
          <label className="mm-filter-label">
            Vak
            <Tooltip text="Filter op een specifiek vak. Selecteer eerst een studie om de lijst te verfijnen." />
          </label>
          <select
            className="form-input"
            value={filterCourse}
            onChange={(e) => { setFilterCourse(e.target.value); setFilterTag(''); }}
            disabled={!filterStudy}
          >
            <option value="">Alle vakken</option>
            {filterCourseOptions.map((c) => (
              <option key={c.id} value={c.id}>{c.code} – {c.name}</option>
            ))}
          </select>
        </div>

        <div className="mm-filter-group">
          <label className="mm-filter-label">
            Docent
            <Tooltip text="Filter op docent die het geselecteerde vak verzorgt." />
          </label>
          <select
            className="form-input"
            value={filterInstructor}
            onChange={(e) => setFilterInstructor(e.target.value)}
          >
            <option value="">Alle docenten</option>
            {instructorOptions.map((instructor) => (
              <option key={instructor.id} value={instructor.id}>{instructor.name}</option>
            ))}
          </select>
        </div>

        <div className="mm-filter-group">
          <label className="mm-filter-label">
            Zoeken
            <Tooltip text="Zoek op titel, beschrijving, vakcode, vaknaam of tags." />
          </label>
          <input
            className="form-input"
            value={searchTerm}
            placeholder="Zoek materiaal..."
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="mm-filter-group">
          <label className="mm-filter-label">
            Type
            <Tooltip text="Filter op materiaaltype zoals document, media, artikel of link." />
          </label>
          <select className="form-input" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
            <option value="">Alle types</option>
            <option value="document">Document</option>
            <option value="media">Media</option>
            <option value="article">Artikel</option>
            <option value="link">Link</option>
          </select>
        </div>

        <div className="mm-filter-group">
          <label className="mm-filter-label">
            Bestandsstatus
            <Tooltip text="Filter op materialen met ontbrekend bestand, of alleen gezonde records." />
          </label>
          <select className="form-input" value={filterFileStatus} onChange={(e) => setFilterFileStatus(e.target.value)}>
            <option value="all">Alle</option>
            <option value="healthy">Bestand OK</option>
            <option value="missing">Ontbreekt</option>
          </select>
        </div>

        <div className="mm-filter-group">
          <label className="mm-filter-label">
            Tag
            <Tooltip text="Filter op een tag die aan het materiaal is gekoppeld. Tags worden ingesteld bij het aanmaken of bewerken van materiaal." />
          </label>
          <select className="form-input" value={filterTag} onChange={(e) => setFilterTag(e.target.value)}>
            <option value="">Alle tags</option>
            {allTags.map((t) => (
              <option key={t} value={t}>#{t}</option>
            ))}
          </select>
        </div>

        <div className="mm-filter-group">
          <label className="mm-filter-label">
            Status
            <Tooltip text="Actief = zichtbaar voor studenten. Inactief = verborgen maar niet verwijderd." />
          </label>
          <select className="form-input" value={filterActive} onChange={(e) => setFilterActive(e.target.value)}>
            <option value="active">Actief</option>
            <option value="inactive">Inactief</option>
            <option value="all">Alle</option>
          </select>
        </div>
      </div>

      {/* Active tag pill */}
      {filterTag && (
        <div className="mm-active-filter-row">
          <span className="mm-active-filter-pill">
            Gefilterd op tag: <strong>#{filterTag}</strong>
            <button className="mm-clear-filter" onClick={() => setFilterTag('')} aria-label="Filter verwijderen">×</button>
          </span>
        </div>
      )}

      {/* Results count */}
      {!loading && (
        <p className="mm-results-count">
          {visibleMaterials.length} {visibleMaterials.length === 1 ? 'materiaal' : 'materialen'} gevonden
        </p>
      )}

      {/* Content */}
      {loading ? (
        <div className="mm-loading">
          <span className="mm-spinner" aria-hidden="true" />
          Leermaterialen laden…
        </div>
      ) : (
        <div className="mm-grid">
          {visibleMaterials.map((item) => {
            const meta = TYPE_META[item.type] || TYPE_META.document;
            return (
              <article className={`mm-card${item.isActive === false ? ' mm-card-inactive' : ''}`} key={item.id}>
                <div className="mm-card-top">
                  <span className="mm-type-badge" style={{ background: meta.bg, color: meta.color }}>
                    {meta.icon} {meta.label}
                  </span>
                  {item.isActive === false && <span className="mm-inactive-badge">Inactief</span>}
                </div>

                <h3 className="mm-card-title">{item.title}</h3>

                {item.file?.filename && item.fileIntegrity?.exists === false && (
                  <span className="mm-missing-file-badge">Bestand ontbreekt</span>
                )}

                <p className="mm-card-course">
                  {item.course?.code
                    ? `${item.course.code} – ${item.course.name}`
                    : item.module?.code
                      ? `${item.module.code} – ${item.module.name}`
                      : <em>Geen vak gekoppeld</em>}
                </p>

                {item.description && <p className="mm-card-desc">{item.description}</p>}

                {/* Tags */}
                {(item.tags || []).length > 0 && (
                  <div className="mm-card-tags">
                    {item.tags.map((tag) => {
                      const tc = tagColor(tag);
                      return (
                        <button
                          key={tag}
                          className="mm-tag mm-tag-clickable"
                          style={{ background: tc.bg, color: tc.color }}
                          onClick={() => setFilterTag(tag)}
                          title={`Filter op #${tag}`}
                          type="button"
                        >
                          #{tag}
                        </button>
                      );
                    })}
                  </div>
                )}

                {/* Article preview */}
                {item.type === 'article' && item.articleContent && (
                  <div className="mm-article-preview" dangerouslySetInnerHTML={{ __html: item.articleContent }} />
                )}

                {/* External link */}
                {item.externalUrl && (
                  <a className="mm-external-link" href={item.externalUrl} target="_blank" rel="noopener noreferrer">
                    🔗 Open externe link
                  </a>
                )}

                {/* File links */}
                {item.file?.filename && (
                  <div className="mm-file-links">
                    <button
                      type="button"
                      className={item.fileIntegrity?.exists === false ? 'mm-link-disabled' : ''}
                      onClick={() => handleFilePreview(item)}
                    >
                      👁 Bekijken
                    </button>
                    <button
                      type="button"
                      className={item.fileIntegrity?.exists === false ? 'mm-link-disabled' : ''}
                      onClick={() => handleFileDownload(item)}
                    >
                      ⬇ Downloaden
                    </button>
                  </div>
                )}

                <div className="mm-card-footer">
                  <span className="mm-sort-order">Volgorde {item.sortOrder}</span>
                  <div className="mm-card-actions">
                    <button type="button" className="btn-sm btn-read" onClick={() => setPreviewItem(item)}>Lezen</button>
                    <button type="button" className="btn-sm btn-outline" onClick={() => openEdit(item)}>✏ Bewerken</button>
                    <button type="button" className="btn-sm btn-danger-sm" onClick={() => removeMaterial(item.id)}>🗑</button>
                  </div>
                </div>
              </article>
            );
          })}

          {visibleMaterials.length === 0 && (
            <div className="mm-empty-state">
              <span className="mm-empty-icon">📂</span>
              <p>Geen leermaterialen gevonden voor de geselecteerde filters.</p>
              <button className="btn-primary" onClick={openCreate}>Materiaal toevoegen</button>
            </div>
          )}
        </div>
      )}

      {/* Modal */}
      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? 'Leermateriaal bewerken' : 'Leermateriaal toevoegen'}
        size="lg"
      >
        <form className="mm-form" onSubmit={saveMaterial}>

          {/* ── Vakkoppeling ── */}
          <fieldset className="mm-fieldset">
            <legend className="mm-legend">📚 Vakkoppeling</legend>

            <div className="mm-field">
              <label className="mm-label">
                Studie
                <Tooltip text="Vernauw het vakkenaanbod door een studie te kiezen. Dit veld wordt niet opgeslagen — het dient alleen als filter zodat je sneller het juiste vak vindt." />
              </label>
              <select
                className="form-input"
                value={form.studyId}
                onChange={(e) => setForm((prev) => ({ ...prev, studyId: e.target.value, courseId: '' }))}
              >
                <option value="">Alle studies (optioneel)</option>
                {[...studiesWithCourses]
                  .sort((a, b) => getStudyOptionLabel(a).localeCompare(getStudyOptionLabel(b), 'nl'))
                  .map((s) => (
                    <option key={s.id} value={s.id}>{getStudyOptionLabel(s)}</option>
                  ))}
              </select>
            </div>

            <div className="mm-field">
              <label className="mm-label">
                Vak <span className="mm-required">*</span>
                <Tooltip text="Het vak waaraan dit materiaal wordt gekoppeld. Studenten die ingeschreven zijn voor dit vak zien het materiaal in hun dashboard." />
              </label>
              <select
                className="form-input"
                value={form.courseId}
                onChange={(e) => setForm((prev) => ({ ...prev, courseId: e.target.value }))}
                required
              >
                <option value="">Kies een vak…</option>
                {formCourseOptions.map((c) => (
                  <option key={c.id} value={c.id}>{c.code} – {c.name}</option>
                ))}
              </select>
            </div>
          </fieldset>

          {/* ── Inhoud ── */}
          <fieldset className="mm-fieldset">
            <legend className="mm-legend">✏ Inhoud</legend>

            <div className="mm-field">
              <label className="mm-label">
                Titel <span className="mm-required">*</span>
                <Tooltip text="De naam van het leermateriaal zoals studenten dit zien in hun vakdashboard. Gebruik een duidelijke, beschrijvende naam." />
              </label>
              <input
                className="form-input"
                placeholder="bv. Hoorcollege week 3 – Inleiding tot marketing"
                value={form.title}
                onChange={(e) => setForm((prev) => ({ ...prev, title: e.target.value }))}
                required
              />
            </div>

            <div className="mm-field">
              <label className="mm-label">
                Beschrijving
                <Tooltip text="Optionele korte samenvatting of toelichting over de inhoud. Studenten zien dit onder de titel als extra context." />
              </label>
              <textarea
                className="form-textarea"
                placeholder="Korte omschrijving van het materiaal (optioneel)…"
                value={form.description}
                onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))}
                rows={3}
              />
            </div>

            <div className="mm-field">
              <label className="mm-label">
                Type materiaal
                <Tooltip text={`📄 Document – upload een bestand (PDF, Word, PPT of Excel)\n🎬 Media – video, audio of externe medialink\n📝 Artikel – typ opgemaakte tekst rechtstreeks in\n🔗 Link – verwijs naar een externe website of pagina`} />
              </label>
              <div className="mm-type-grid">
                {Object.entries(TYPE_META).map(([val, meta]) => (
                  <label
                    key={val}
                    className={`mm-type-option${form.type === val ? ' mm-type-selected' : ''}`}
                    style={form.type === val ? { background: meta.bg, borderColor: meta.color, color: meta.color } : {}}
                  >
                    <input
                      type="radio"
                      name="materialType"
                      value={val}
                      checked={form.type === val}
                      onChange={() => setForm((prev) => ({ ...prev, type: val }))}
                    />
                    <span className="mm-type-icon">{meta.icon}</span>
                    <span>{meta.label}</span>
                  </label>
                ))}
              </div>
            </div>

            {form.type === 'article' && (
              <div className="mm-field">
                <label className="mm-label">
                  Artikel inhoud
                  <Tooltip text="Schrijf de volledige tekst van het artikel. Gebruik de werkbalk voor opmaak zoals vetgedrukt, koppen en lijsten." />
                </label>
                <RichTextEditor
                  value={form.articleContent}
                  onChange={(value) => setForm((prev) => ({ ...prev, articleContent: value }))}
                  placeholder="Schrijf hier het artikel…"
                  minHeight={220}
                />
              </div>
            )}

            {(form.type === 'link' || form.type === 'media') && (
              <div className="mm-field">
                <label className="mm-label">
                  Externe URL
                  <Tooltip text="Volledige weblink inclusief https://. Studenten worden naar deze pagina doorgestuurd wanneer ze op het materiaal klikken." />
                </label>
                <input
                  className="form-input"
                  placeholder="https://..."
                  value={form.externalUrl}
                  onChange={(e) => setForm((prev) => ({ ...prev, externalUrl: e.target.value }))}
                />
              </div>
            )}

            {form.type !== 'article' && (
              <div className="mm-field">
                <label className="mm-label">
                  Bestand uploaden
                  <Tooltip text="Ondersteunde typen: PDF, Word (.docx), PowerPoint (.pptx), Excel (.xlsx), afbeeldingen (JPG/PNG/WebP), MP4 en MP3. Een nieuw bestand vervangt het vorige." />
                </label>
                <input
                  className="form-input mm-file-input"
                  type="file"
                  accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.webp,.mp4,.mp3"
                  onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                />
                {uploadFile && <p className="mm-file-chosen">Gekozen: <strong>{uploadFile.name}</strong></p>}
              </div>
            )}
          </fieldset>

          {/* ── Tags ── */}
          <fieldset className="mm-fieldset">
            <legend className="mm-legend">🏷 Tags &amp; Categorisering</legend>

            <div className="mm-field">
              <label className="mm-label">
                Tags
                <Tooltip text="Voeg trefwoorden toe zodat beheerders materiaal snel kunnen filteren. Typ een tag en druk op Enter of komma. Voorbeelden: toets, hoorcollege, week-3, verplicht, extra." />
              </label>
              <div className="mm-tag-input-wrap">
                {form.tags.map((tag) => {
                  const tc = tagColor(tag);
                  return (
                    <span key={tag} className="mm-tag mm-tag-removable" style={{ background: tc.bg, color: tc.color }}>
                      #{tag}
                      <button
                        type="button"
                        className="mm-tag-remove"
                        onClick={() => removeTag(tag)}
                        aria-label={`Verwijder tag ${tag}`}
                      >×</button>
                    </span>
                  );
                })}
                <input
                  className="mm-tag-input"
                  type="text"
                  placeholder={form.tags.length === 0 ? 'Tag toevoegen… (Enter of komma)' : 'Nog een tag…'}
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleTagKeyDown}
                  onBlur={commitTag}
                />
              </div>
              <p className="mm-field-hint">Druk op <kbd>Enter</kbd> of <kbd>,</kbd> om een tag te bevestigen · <kbd>Backspace</kbd> verwijdert de laatste tag.</p>
            </div>
          </fieldset>

          {/* ── Publicatie ── */}
          <fieldset className="mm-fieldset">
            <legend className="mm-legend">⚙ Publicatie-instellingen</legend>

            <div className="mm-field-row">
              <div className="mm-field">
                <label className="mm-label">
                  Volgorde
                  <Tooltip text="Numerieke sorteervolgorde binnen het vak. Een lager getal plaatst het materiaal hoger in de lijst. Bij gelijke volgorde wordt op aanmaakdatum gesorteerd." />
                </label>
                <input
                  className="form-input"
                  type="number"
                  min="0"
                  placeholder="0"
                  value={form.sortOrder}
                  onChange={(e) => setForm((prev) => ({ ...prev, sortOrder: Number(e.target.value) }))}
                />
              </div>

              <div className="mm-field">
                <label className="mm-label">
                  Status
                  <Tooltip text="Actief: het materiaal is zichtbaar voor ingeschreven studenten.\nInactief: het materiaal is verborgen maar blijft bewaard. Gebruik dit om materiaal tijdelijk weg te halen." />
                </label>
                <select
                  className="form-input"
                  value={String(form.isActive)}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: parseBool(e.target.value) }))}
                >
                  <option value="true">✅ Actief – zichtbaar voor studenten</option>
                  <option value="false">⛔ Inactief – verborgen voor studenten</option>
                </select>
              </div>
            </div>
          </fieldset>

          {error && <p className="mm-form-error">{error}</p>}

          <div className="mm-form-actions">
            <button type="submit" className="btn-primary">
              {editingId ? '💾 Wijzigingen opslaan' : '＋ Materiaal toevoegen'}
            </button>
            <button type="button" className="btn-secondary" onClick={() => setShowModal(false)}>Annuleren</button>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={Boolean(previewItem)}
        onClose={() => setPreviewItem(null)}
        title={previewItem?.title || 'Materiaal bekijken'}
        size="lg"
      >
        {previewItem && (
          <div className="mm-preview">
            <p className="mm-preview-meta">
              {previewItem.course?.code ? `${previewItem.course.code} – ${previewItem.course.name}` : 'Geen vak gekoppeld'}
            </p>
            {previewItem.description && <p className="mm-preview-desc">{previewItem.description}</p>}

            {previewItem.type === 'article' && previewItem.articleContent && (
              <div className="mm-preview-article" dangerouslySetInnerHTML={{ __html: previewItem.articleContent }} />
            )}

            {previewItem.externalUrl && (
              <p>
                <a className="mm-external-link" href={previewItem.externalUrl} target="_blank" rel="noopener noreferrer">
                  Open externe bron
                </a>
              </p>
            )}

            {previewItem.file?.filename && (
              <div className="mm-preview-actions">
                <button type="button" className="btn-secondary" onClick={() => handleFilePreview(previewItem)}>Bekijken</button>
                <button type="button" className="btn-primary" onClick={() => handleFileDownload(previewItem)}>Downloaden</button>
              </div>
            )}
          </div>
        )}
      </Modal>

      <PDFViewerModal
        isOpen={isPdfViewerOpen}
        onClose={closePdfViewer}
        pdfUrl={pdfViewerUrl}
        title={pdfViewerTitle}
        showDownload
        onDownload={() => handleFileDownload(pdfViewerItemId)}
      />
    </div>
  );
};

export default ModuleMaterials;
